import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { US_STATES } from '../shared/models/us-states';
import { ToastrService } from 'ngx-toastr';
import { InsureApiService } from '../shared/services/insure-api.service';
import {
  IdentityApiService,
  RegisterUserDto,
} from '../shared/services/identity-api.service';
import { UserRole } from '../shared/models/user-role.enum';
import { switchMap } from 'rxjs';
import { stripFormValues } from '../shared/utils/strip-characters';
import { PASSWORD_PATTERN } from '../shared/utils/password-validation';
import { environment } from '../../environments/environment';

@Component({
  selector: 'ins-register-agent',
  templateUrl: './register-agent.component.html',
})
export class RegisterAgentComponent implements OnInit {
  @ViewChild('errorAlert') errorAlert!: ElementRef;
  registerForm!: FormGroup;
  newAgentForm!: FormGroup;
  states = US_STATES;
  errorMessage = '';
  registerSubmitted = false;
  captchaIsValid = false;
  captchaResponse = '';
  captchaKey = environment.RecaptchaSiteKey;

  constructor(
    private fb: FormBuilder,
    private toastr: ToastrService,
    private insureApi: InsureApiService,
    private identityApi: IdentityApiService,
  ) {}

  ngOnInit(): void {
    this.registerForm = this.fb.group({
      userName: ['', Validators.required],
      agencyNumber: ['', Validators.required],
      agencyName: ['', Validators.required],
      contactName: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      address1: ['', Validators.required],
      address2: [''],
      city: ['', Validators.required],
      state: ['', Validators.required],
      zipCode: ['', Validators.required],
      password: [
        '',
        [Validators.required, Validators.pattern(PASSWORD_PATTERN)],
      ],
      confirmPassword: ['', Validators.required],
    });

    this.newAgentForm = this.fb.group({
      name: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      phone: ['', Validators.required],
    });
  }

  get passwordMismatch(): boolean {
    return (
      this.registerForm.controls['password'].value !==
        this.registerForm.controls['confirmPassword'].value &&
      this.registerForm.controls['confirmPassword'].touched
    );
  }

  onAgencyNumberBlur(): void {
    const agencyNumber =
      this.registerForm.controls['agencyNumber'].value?.trim();
    if (!agencyNumber) {
      return;
    }

    this.errorMessage = '';
    this.insureApi.getAgency(agencyNumber).subscribe({
      next: (agency) => {
        if (agency) {
          this.registerForm.patchValue({
            agencyName: agency.agencyName || '',
            contactName: agency.agencyContactName || '',
            email: '',
            address1: agency.agencyAddress || '',
            address2: agency.agencySecondaryAddress || '',
            city: agency.agencyCity || '',
            state: agency.agencyState || '',
            zipCode: agency.agencyZipCode || '',
          });
        } else {
          this.clearAgencyFields();
          this.errorMessage =
            'Agency number you are trying to enter is not valid. Please contact us at 1.800.444.3584 for more help. Thank you.';
        }
      },
      error: () => {
        this.clearAgencyFields();
        this.errorMessage =
          'Agency number you are trying to enter is not valid. Please contact us at 1.800.444.3584 for more help. Thank you.';
      },
    });
  }

  private clearAgencyFields(): void {
    this.registerForm.patchValue({
      agencyName: '',
      contactName: '',
      email: '',
      address1: '',
      address2: '',
      city: '',
      state: '',
      zipCode: '',
    });
  }

  onRegister(): void {
    this.registerSubmitted = true;
    this.registerForm.markAllAsTouched();
    if (this.registerForm.invalid || this.passwordMismatch || !this.captchaIsValid) {
      return;
    }

    this.errorMessage = '';
    const f = stripFormValues(this.registerForm.value, [
      'password',
      'confirmPassword',
      'state',
    ]);

    const user: RegisterUserDto = {
      userName: f.userName,
      email: f.email,
      name: f.contactName,
      address: f.address1,
      address2: f.address2 || '',
      city: f.city,
      state: f.state,
      postalCode: f.zipCode,
      company: f.agencyName,
      password: f.password,
      roles: [UserRole.Agent],
    };

    this.identityApi
      .registerUser(user)
      .pipe(
        switchMap((registeredUser) =>
          this.insureApi.insertAgent({
            userName: f.userName,
            agencyNumber: f.agencyNumber,
            agencyName: f.agencyName,
            agencyContactName: f.contactName,
            contactEmailAddress: f.email,
            agencyAddress1: f.address1,
            agencyAddress2: f.address2 || '',
            agencyCity: f.city,
            agencyState: f.state,
            agencyZipCode: f.zipCode,
            systemId: registeredUser.id,
          }),
        ),
      )
      .subscribe({
        next: () => {
          this.sendRegistrationNotification(f);
          this.toastr.success(
            'Successful Registration. We will contact you within 12 to 24 hours with your access information.',
          );
          this.registerForm.reset();
          this.registerSubmitted = false;
          this.errorMessage = '';
        },
        error: (err) => {
          if (err.status === 409 || err.status === 400) {
            this.errorMessage =
              'You are already registered in our system. If you have forgotten login or password, please contact us at 1.800.444.3584. Thank you.';
          } else {
            this.errorMessage =
              'There was an error processing your registration. Please try again.';
          }
          this.scrollToError();
        },
      });
  }

  onNewAgentSubmit(): void {
    this.newAgentForm.markAllAsTouched();
    if (this.newAgentForm.invalid) {
      return;
    }

    const f = this.newAgentForm.value;
    let body =
      'A request to become an Authorized Agent has been submitted from DFS Insurance website.';
    body +=
      '<br/><br/>Name: ' +
      f.name +
      ' can be reached at ' +
      f.email +
      ' and Phone Number: ' +
      f.phone;

    this.insureApi
      .sendEmail({
        toAddresses: ['jtaylor@dfsinsure.com', 'mmoragues@dfsinsure.com'],
        subject: 'Authorized Agent Request',
        bodyHtml: body,
      })
      .subscribe({
        next: () => {
          this.toastr.success(
            'Your request has been submitted. We will be in touch shortly.',
          );
          this.newAgentForm.reset();
        },
        error: () => {
          this.toastr.error(
            'There was an error emailing the request. Please use contact info to manually get signed up.',
          );
        },
      });
  }

  private sendRegistrationNotification(f: any): void {
    const body =
      'Agent register on ' +
      new Date().toLocaleString() +
      '<br/><br/>Username: ' +
      f.userName +
      '<br/><br/>Agency Number: ' +
      f.agencyNumber +
      '<br/><br/>Agency Contact Name: ' +
      f.contactName +
      '<br/>Agency Name: ' +
      f.agencyName +
      '<br/>Agency Address: ' +
      f.address1 +
      ', ' +
      f.city +
      ', ' +
      f.state +
      ', ' +
      f.zipCode;

    this.insureApi
      .sendEmail({
        toAddresses: ['DACInsQuoteRequest@dfsfin.com'],
        subject:
          'Agent registration [' +
          f.agencyNumber +
          '--' +
          f.agencyName +
          '] notification from DACIns website',
        bodyHtml: body,
      })
      .subscribe();
  }

  getCaptchaValue(captcha: string | null): void {
    this.captchaIsValid = !(captcha === null || captcha === undefined);
    this.captchaResponse = captcha || '';
  }

  private scrollToError(): void {
    setTimeout(() => {
      this.errorAlert?.nativeElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }
}
